/* ==================================================================
   Ponte do motor para o navegador — Reflect On Engenharia
   ------------------------------------------------------------------
   Este arquivo NÃO é escrito à mão no `public/`. Ele é a entrada de um
   bundle gerado por `npm run motor`, que compila `lib/motor/` inteiro
   em `public/motor.js` e o expõe como `window.AgenteMotor`.

   Por que gerar em vez de manter uma cópia: as regras deste motor
   mudaram duas vezes só na última semana (gás entrando no escopo, o
   memorial deixando de ser imposto). Duas cópias digitadas divergem na
   terceira mudança — e a divergência aqui não dá erro, ela só faz a
   página anunciar um preço e o servidor cobrar outro.

   ⚠️ O QUE O NAVEGADOR CALCULA NÃO TEM AUTORIDADE NENHUMA. Este bundle
   existe para a conta abrir ao vivo enquanto a pessoa marca as opções,
   que é peça de venda. Quem grava o pedido e monta a sessão do Stripe é
   o servidor, que refaz a conta do zero a partir das respostas. Se os
   dois divergirem, o servidor ganha e a interface se corrige — o
   `fechar()` do widget já sabe explicar isso em voz alta.
   ================================================================== */

import { classificar, normalizarRespostas } from "./index";
import { PASSOS } from "./passos";
import { descricaoTecnica } from "./descricao";
import {
  TABELA_VIGENTE,
  type Tabela,
  brl,
  contaDaFaixa,
  emCentavos,
} from "./tabela";
import type { Respostas, Resultado } from "./tipos";
import { WHATSAPP } from "@/lib/contato";
import { RESUMO_ACEITE, VERSAO_TERMOS } from "@/lib/termos";

/* A tabela ativa do lado do navegador. Começa na constante compilada e pode
   ser trocada em tempo de execução por `usarTabela()`.

   Esse encaixe existe pro bloco 2: quando `tabela_crea` passar a morar no
   banco, a página vai injetar a tabela vigente e chamar `usarTabela()`, e o
   preview passa a refletir a tabela de verdade sem ninguém tocar no widget.
   Sem esse encaixe, a virada da tabela do CREA deixaria o preview congelado
   nos valores do último build — que é a divergência silenciosa de novo, só
   que por outro caminho. */
let tabelaAtiva: Tabela = TABELA_VIGENTE;

const AgenteMotor = {
  PASSOS,

  /** Classifica com a tabela ativa. O segundo parâmetro do motor fica de
   *  fora de propósito: no navegador ninguém escolhe tabela. */
  classificar(respostas: Respostas): Resultado {
    return classificar(respostas, tabelaAtiva);
  },

  /** A conta aberta de uma faixa. Usada pela calculadora da home. */
  conta(acima: boolean, comMemorial: boolean) {
    return contaDaFaixa(acima, comMemorial, tabelaAtiva);
  },

  /** Troca a tabela vigente do preview. Chamado pela página no bloco 2. */
  usarTabela(t: Tabela) {
    tabelaAtiva = t;
  },

  normalizarRespostas,
  brl,
  emCentavos,
  WHATS: WHATSAPP,

  /** O texto da ART. Mesma função que o painel usa no servidor, desde 14/09. */
  descricaoTecnica,

  /* ---- nomes que o widget já usa há semanas ----
     São getters, não cópias: se `usarTabela()` trocar a tabela, estes
     acompanham. Constante congelada aqui seria o mesmo bug, em miniatura. */
  get TABELA(): Tabela {
    return tabelaAtiva;
  },
  get MEMORIAL(): number {
    return tabelaAtiva.memorial;
  },
  get TAXA_ATE_15K(): number {
    return tabelaAtiva.creaAte15k.valorTaxa;
  },
  get TAXA_ACIMA(): number {
    return tabelaAtiva.creaAcima15k.valorTaxa;
  },
};

/* O aceite de responsabilidade viaja no MESMO bundle, mas em um global
   próprio: preço e termo jurídico são assuntos diferentes, e enfiar o
   segundo dentro de `AgenteMotor` faria o nome do módulo mentir.

   Vem daqui, e não escrito à mão no `legacy.js`, pelo mesmo motivo do
   preço: o widget mostra o resumo, a página `/termos` mostra o texto
   integral, e o servidor grava a versão aceita. Três redações do mesmo
   termo divergem, e aí o cliente aceita uma coisa e a página publica
   outra. `lib/termos.ts` é puro, sem banco e sem rede, então entra no
   bundle sem arrastar servidor junto. */
const TermosAceite = {
  VERSAO: VERSAO_TERMOS,
  RESUMO: RESUMO_ACEITE,
};

declare global {
  interface Window {
    AgenteMotor: typeof AgenteMotor;
    TermosAceite: typeof TermosAceite;
  }
}

window.AgenteMotor = AgenteMotor;
window.TermosAceite = TermosAceite;

export type AgenteMotorGlobal = typeof AgenteMotor;
